import { clusterIdOf, counterLabelsOf, defaultMinHoldMs, } from "./word-set.js";
/**
 * Segmentation + sequence alignment (idea.md §7.2–7.3.1).
 *
 * This is the model-free, cold-start-safe path: phones whose model state is
 * `calibrated`/`emerging` (in practice the scaffolds) are located as runs of
 * matching classifier labels and act as anchors; every other phone is
 * assigned the residual gap between its neighbouring anchors, snapped to a
 * local energy minimum. Omission is only ever *suggested* for modeled
 * phones; an unmodeled target is "about here", never "missing".
 */
const SNAP_WINDOW_MS = 40;
const MIN_ANCHOR_FRAMES_SUSTAINED = 2;
function isModeled(s) {
    return s === "calibrated" || s === "emerging";
}
function estimateFrameMs(frames) {
    if (frames.length < 2)
        return 10;
    const d = (frames[frames.length - 1].tMs - frames[0].tMs) / (frames.length - 1);
    return d > 0 ? d : 10;
}
function findRun(frames, label, fromMs, toMs, need, frameMs) {
    let run = [];
    for (const f of frames) {
        if (f.tMs < fromMs)
            continue;
        if (f.tMs > toMs)
            break;
        if (f.label === label) {
            run.push(f);
        }
        else {
            if (run.length >= need)
                break;
            run = [];
        }
    }
    if (run.length < need)
        return null;
    const conf = run.reduce((a, f) => a + f.confidence, 0) / run.length;
    return { t0: run[0].tMs, t1: run[run.length - 1].tMs + frameMs, conf };
}
function snapToEnergyMin(frames, tMs, lo, hi) {
    let best = tMs;
    let bestE = Infinity;
    for (const f of frames) {
        if (Math.abs(f.tMs - tMs) > SNAP_WINDOW_MS)
            continue;
        if (f.tMs <= lo || f.tMs >= hi)
            continue;
        if (f.energy < bestE) {
            bestE = f.energy;
            best = f.tMs;
        }
    }
    return best;
}
function dominantLabel(frames, t0, t1) {
    const counts = new Map();
    let n = 0;
    for (const f of frames) {
        if (f.tMs < t0 || f.tMs >= t1)
            continue;
        n++;
        if (f.label)
            counts.set(f.label, (counts.get(f.label) ?? 0) + 1);
    }
    let label = null;
    let best = 0;
    for (const [l, c] of counts) {
        if (c > best) {
            best = c;
            label = l;
        }
    }
    return { label, share: n > 0 ? best / n : 0 };
}
export function segmentAttempt(input) {
    const { utterance: u, word, phones, frames } = input;
    const seq = word.sequence;
    const n = seq.length;
    if (n === 0)
        return [];
    const frameMs = estimateFrameMs(frames);
    const states = seq.map((e) => input.modelStateOf(e.phone));
    // 1. Anchors: monotonic, left-to-right, only for modeled phones.
    const anchors = new Array(n).fill(null);
    let cursor = u.t0;
    for (let i = 0; i < n; i++) {
        const phone = phones.get(seq[i].phone);
        const cid = clusterIdOf(phone);
        if (!phone || !cid || !isModeled(states[i]))
            continue;
        const need = phone.kind === "percussive" ? 1 : MIN_ANCHOR_FRAMES_SUSTAINED;
        const run = findRun(frames, cid, cursor, u.t1, need, frameMs);
        if (!run)
            continue;
        anchors[i] = run;
        cursor = run.t1;
    }
    // 2. Fill gaps between anchors proportionally to minHoldMs.
    const segs = new Array(n);
    let i = 0;
    while (i < n) {
        if (anchors[i]) {
            segs[i] = makeSegment(i, anchors[i], "posterior", false);
            i++;
            continue;
        }
        let j = i;
        while (j < n && !anchors[j])
            j++;
        const gap0 = i === 0 ? u.t0 : anchors[i - 1].t1;
        const gap1 = j >= n ? u.t1 : anchors[j].t0;
        const span = Math.max(0, gap1 - gap0);
        const weights = [];
        for (let k = i; k < j; k++) {
            weights.push(defaultMinHoldMs(phones.get(seq[k].phone)));
        }
        const total = weights.reduce((a, b) => a + b, 0) || 1;
        const shared = j - i > 1;
        let acc = gap0;
        for (let k = i; k < j; k++) {
            const s0 = acc;
            let s1 = k === j - 1 ? gap1 : acc + (span * weights[k - i]) / total;
            if (k < j - 1)
                s1 = snapToEnergyMin(frames, s1, s0, gap1);
            s1 = Math.max(s0, s1);
            acc = s1;
            segs[k] = makeSegment(k, { t0: s0, t1: s1, conf: 0 }, "gap", shared);
        }
        i = j;
    }
    return segs;
    function makeSegment(idx, span, basis, shared) {
        const entry = seq[idx];
        const phone = phones.get(entry.phone);
        const state = states[idx];
        const dom = dominantLabel(frames, span.t0, span.t1);
        let suggested = null;
        if (basis === "posterior") {
            // Only a calibrated model may suggest "correct"; emerging stays quiet.
            if (state === "calibrated")
                suggested = "correct";
        }
        else if (isModeled(state)) {
            // Modeled but no acceptable emission anywhere → suggest omission.
            suggested = "omitted";
        }
        else {
            // §7.3.1 step 4: counter-label first — needs no target model at all.
            for (const cid of counterLabelsOf(phone)) {
                const subCluster = clusterIdOf(phones.get(cid));
                if (!subCluster || !isModeled(input.modelStateOf(cid)))
                    continue;
                if (dom.label === subCluster && dom.share > 0.5) {
                    suggested = `substituted:${cid}`;
                    break;
                }
            }
        }
        return {
            phoneId: entry.phone,
            role: entry.role,
            t0: span.t0,
            t1: span.t1,
            auto: {
                modelState: state,
                basis,
                topLabel: dom.label,
                confidence: basis === "posterior" ? span.conf : null,
                suggested,
                lowConfidence: basis === "gap" && (shared || span.t1 - span.t0 <= 0),
            },
            tag: null,
        };
    }
}
export function computeCompletion(word, segs) {
    let expected = 0;
    let matched = 0;
    let posterior = 0;
    let gap = 0;
    for (let i = 0; i < word.sequence.length; i++) {
        if (word.sequence[i].role === "optional")
            continue;
        expected++;
        const s = segs[i];
        if (!s || s.t1 - s.t0 <= 0)
            continue;
        matched++;
        if (s.auto.basis === "posterior")
            posterior++;
        else
            gap++;
    }
    const basis = matched === 0 ? "none" : gap === 0 ? "posterior" : posterior === 0 ? "gap" : "mixed";
    return { value: expected > 0 ? matched / expected : 0, basis };
}
